import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';

export enum PopoverAlignment {
  Left,
  Right,
  Center,
}

interface PopoverProps {
  popoverToggle: React.ReactNode;
  children: React.ReactNode;
  alignment?: PopoverAlignment;
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
  width?: number;
  height?: number;
  edgePadding?: number;
}

export const Popover = ({
  popoverToggle,
  children,
  alignment = PopoverAlignment.Left,
  className,
  isOpen = false,
  onClose,
  width = 300,
  height = 250,
  edgePadding = 16,
}: PopoverProps) => {
  const transitionTime = 300;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(isOpen);
  const [render, setRender] = useState(isOpen);
  const [effectiveAlignment, setEffectiveAlignment] = useState(alignment);
  const [contentMaxWidth, setContentMaxWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!render || !contentRef.current || !containerRef.current) {
      setEffectiveAlignment(alignment);
      setContentMaxWidth(undefined);
      return;
    }
    const contentWidth = contentRef.current.offsetWidth;
    const { left: toggleLeft, right: toggleRight } = containerRef.current.getBoundingClientRect();
    const spaceRight = window.innerWidth - edgePadding - toggleLeft;
    const spaceLeft = toggleRight - edgePadding;

    let newAlignment: PopoverAlignment;
    if (alignment === PopoverAlignment.Center) {
      const toggleCenter = (toggleLeft + toggleRight) / 2;
      const fitsCenter =
        toggleCenter - contentWidth / 2 >= edgePadding &&
        toggleCenter + contentWidth / 2 <= window.innerWidth - edgePadding;
      newAlignment = fitsCenter
        ? PopoverAlignment.Center
        : spaceRight >= spaceLeft
        ? PopoverAlignment.Left
        : PopoverAlignment.Right;
    } else if (alignment === PopoverAlignment.Left) {
      newAlignment =
        contentWidth <= spaceRight || spaceRight >= spaceLeft
          ? PopoverAlignment.Left
          : PopoverAlignment.Right;
    } else {
      newAlignment =
        contentWidth <= spaceLeft || spaceLeft >= spaceRight
          ? PopoverAlignment.Right
          : PopoverAlignment.Left;
    }

    let maxW: number;
    if (newAlignment === PopoverAlignment.Left) {
      maxW = spaceRight;
    } else if (newAlignment === PopoverAlignment.Right) {
      maxW = spaceLeft;
    } else {
      const toggleCenter = (toggleLeft + toggleRight) / 2;
      maxW =
        Math.min(toggleCenter - edgePadding, window.innerWidth - edgePadding - toggleCenter) * 2;
    }

    setEffectiveAlignment(newAlignment);
    setContentMaxWidth(maxW);
  }, [render, edgePadding, alignment]);

  const closePopover = useCallback(() => {
    setOpen(false);
    setTimeout(() => setRender(false), transitionTime);
  }, []);

  const handleFocusOut = (e: React.FocusEvent) => {
    const blurTarget = e.target as Node;
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        if (!document.contains(blurTarget)) {
          // The element that lost focus was removed from the DOM (e.g. a placement toggle
          // unmounted a child). Re-focus the content area so the popover stays open and
          // a subsequent real focus-out will still close it.
          contentRef.current?.focus();
        } else {
          closePopover();
          onClose?.();
        }
      }
    }, 0);
  };

  useEffect(() => {
    if (!render) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        closePopover();
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [render, closePopover, onClose]);

  useEffect(() => {
    if (isOpen) {
      setRender(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setOpen(true));
      });
      return () => cancelAnimationFrame(raf);
    } else {
      closePopover();
    }
  }, [isOpen]);
  return (
    <div
      className={`tmys-popover${open ? ' open' : ''}${className ? ' ' + className : ''}`}
      ref={containerRef}
      onBlur={handleFocusOut}
    >
      <div className="popover-toggle">{popoverToggle}</div>
      {render && (
        <div
          ref={contentRef}
          className={`popover-content${
            effectiveAlignment === PopoverAlignment.Right ? ' popover-align-right' : ''
          }${effectiveAlignment === PopoverAlignment.Center ? ' popover-align-center' : ''}`}
          tabIndex={-1}
          style={
            {
              '--popoverWidth': `${width}px`,
              '--popoverHeight': `${height}px`,
              '--transitionTime': `${transitionTime}ms`,
              maxWidth: contentMaxWidth !== undefined ? `${contentMaxWidth}px` : undefined,
            } as React.CSSProperties
          }
        >
          {children}
        </div>
      )}
    </div>
  );
};
